import { cn } from '@commenda/utils';
import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons';
import * as React from 'react';
import { DayPicker } from 'react-day-picker';

type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={className}
      classNames={{
        months: 'flex flex-col space-y-3',
        month: 'space-y-3',
        caption: 'flex justify-center relative items-center',
        caption_label: 'txt-medium-bold text-center text-fg-secondary',
        nav: 'flex items-center bg-red-300',
        nav_button:
          'h-7 w-7 bg-transparent p-2 text-fg-quaternary hover:text-fg-quaternary-hover',
        nav_button_previous:
          'absolute left-0 top-1/2 -translate-y-1/2 flex justify-center items-center',
        nav_button_next:
          'absolute right-0 top-1/2 -translate-y-1/2 flex justify-center items-center',
        table:
          'w-full border-collapse space-y-1 align-middle flex flex-col items-center',
        head_row: 'flex gap-0.5',
        head_cell:
          'rounded-full h-10 w-10 shrink-0 flex justify-center items-center txt-small-medium text-text-secondary',
        row: 'flex w-full gap-0.5',
        cell: cn(
          'relative h-10 w-10 flex justify-center items-center text-center text-small-regular rounded-full hover:bg-bg-active-light transition-all duration-200 focus-within:relative focus-within:z-20 focus-within:border-none [&:has([aria-selected])]:bg-fg-brand [&:has([aria-selected])]:text-text-white [&:has([aria-selected].day-outside)]:bg-bg-active-light [&:has([aria-selected].day-outside)]:text-button-secondary-fg [&:has([aria-selected].day-range-end)]:rounded-r-md',
          props.mode === 'range'
            ? '[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md'
            : '[&:has([aria-selected])]:rounded-full'
        ),
        day: 'h-10 w-10 p-0 txt-small-regular font-normal aria-selected:opacity-100 shrink-0',
        day_range_start: 'day-range-start',
        day_range_end: 'day-range-end',
        day_outside:
          'day-outside text-muted-foreground opacity-50  aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30',
        day_disabled: 'text-muted-foreground opacity-50',
        day_range_middle:
          'aria-selected:bg-accent aria-selected:text-accent-foreground',
        day_hidden: 'invisible',
        ...classNames,
      }}
      components={{
        IconLeft: () => (
          <ChevronLeftIcon className="size-4 shrink-0 text-fg-secondary hover:text-fg-secondary-hover" />
        ),
        IconRight: () => (
          <ChevronRightIcon className="size-4 shrink-0 text-fg-secondary hover:text-fg-secondary-hover" />
        ),
      }}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
